import { Box, Button, Stack, Typography } from "@mui/material";
import {
  Children,
  FC,
  PropsWithChildren,
  ReactNode,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";

import __ from "web/Layout/Translations";

import Dialog from "web/Components/mui/Dialog";

import { useAppContext } from "web/context/app";

interface ISubscriptionSummaryProps {
  name: string;
  quantity: number;
  additionalPersonInfo?: ReactNode;
  showAdditionalPersonInfo?: boolean;
}

const getTextContent = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return Children.toArray(node).map(getTextContent).join("");
};

const SubscriptionSummaryMUI: FC<
  PropsWithChildren<ISubscriptionSummaryProps>
> = ({
  children,
  name,
  quantity,
  additionalPersonInfo,
  showAdditionalPersonInfo,
}) => {
  const { isMobile } = useAppContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const shouldShowAdditionalPersonInfo = !!(
    showAdditionalPersonInfo && additionalPersonInfo
  );
  const additionalPersonInfoText = getTextContent(additionalPersonInfo);

  useEffect(() => {
    if (!shouldShowAdditionalPersonInfo) return undefined;

    const checkTruncation = () => {
      const element = textRef.current;
      if (element) setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [shouldShowAdditionalPersonInfo, additionalPersonInfoText]);

  return (
    <Box
      sx={{
        borderBottom: "1px solid #EEE",
        mb: 2,
        "&:last-child": {
          borderBottom: "0px",
          mb: 0,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#E1F5FE",
          borderRadius: "16px",
          px: 2,
          py: 0.8,
          mx: { xs: 2, md: 3 },
        }}
      >
        <Typography variant="subtitle1">{name}</Typography>
        {shouldShowAdditionalPersonInfo && (
          <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              ref={textRef}
              variant="body2"
              noWrap
              sx={{ minWidth: 0 }}
            >
              {additionalPersonInfoText}
            </Typography>
            {isTruncated && (
              <Button
                variant="text"
                size="small"
                onClick={() => setDialogOpen(true)}
                sx={{ flexShrink: 0, ml: 1, minWidth: "auto", p: 0 }}
              >
                {__("czytaj więcej")}
              </Button>
            )}
          </Stack>
        )}
        <Stack direction="row" alignItems="center">
          <Typography variant="body2">{__("Liczba osób:")}</Typography>
          <Typography variant="subtitle2" sx={{ ml: 0.5 }}>
            {quantity}
          </Typography>
        </Stack>
      </Box>
      {children}

      {shouldShowAdditionalPersonInfo && (
        <Dialog
          fullScreen={isMobile}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={__("Ważne informacje dotyczące abonamentu")}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {name}
          </Typography>
          <Typography className="cke_editable cke_editable_themed cke_contents_ltr cke_show_borders">
            {additionalPersonInfo}
          </Typography>
        </Dialog>
      )}
    </Box>
  );
};

export default SubscriptionSummaryMUI;
